/**
 * "Brief zeigen" (2026-09-19, past-plans/jazzy-weaving-knuth.md): der Brief ist die
 * Pflicht-Vorschau vor der teuren Generierung. Zwei reine Helfer, ohne Store und React:
 *
 *  - `briefEingabeKey` spiegelt clientseitig, was der Server in `brief.eingabe` vergleicht
 *    (services/linkedin_brief.brief_eingabe). Aendert sich der Key nach dem Zeigen, ist der
 *    Brief veraltet: der Kasten sagt es SOFORT, nicht erst der 409 vom Server.
 *  - `briefRichtungZeile` ist die Klartext-Zeile, an der man auf einen Blick sieht, ob die
 *    Richtung stimmt -- statt des langen Label-Blocks im Thema-Feld (vaultHandover).
 */
import type { LinkedInBrief } from '../types/linkedin';
import type { VaultSourceMeta } from '../store/linkedinSlice';

export interface BriefEingabeForm {
  strategyId: number | null;
  topicAngle: string;
  language: string;
  briefLeadAggregat: boolean;
  briefOutreachAntworten: boolean;
  briefTelefonNotizen: boolean;
  vaultSourcePostId: number | null;
  vaultSourceMode: string;
}

export function briefEingabeKey(form: BriefEingabeForm): string {
  return JSON.stringify([
    form.strategyId,
    form.topicAngle.trim(),
    form.language,
    form.briefLeadAggregat,
    form.briefOutreachAntworten,
    form.briefTelefonNotizen,
    form.vaultSourcePostId,
    form.vaultSourcePostId ? form.vaultSourceMode : null,
  ]);
}

const VAULT_PRAEFIX = /^Inspiration from Content Vault\s*[—-]\s*[^\n]*\n*/;

/** Erste brauchbare Zeile des Themas, ohne den Vault-Vorspann und ohne "Label:"-Etikett. */
export function themaKurz(topicAngle: string, max = 140): string {
  const ohne = topicAngle.replace(VAULT_PRAEFIX, '').trim();
  const zeile = ohne.split('\n').map((l) => l.trim()).find((l) => l.length > 0) ?? '';
  const ohneLabel = zeile.replace(/^(Topic|Thema|Angle|Core insight|Summary):\s*/i, '');
  return ohneLabel.length <= max ? ohneLabel : `${ohneLabel.slice(0, max).trimEnd()}…`;
}

export interface BriefRichtung {
  richtung: string;
  quelle: string | null;
}

export function briefRichtungZeile(
  brief: Pick<LinkedInBrief, 'kernaussage'> | null,
  form: { topicAngle: string; vaultSourceMeta: VaultSourceMeta | null },
): BriefRichtung {
  const kern = (brief?.kernaussage ?? '').trim();
  const richtung = kern || themaKurz(form.topicAngle) || '(kein Thema)';
  const meta = form.vaultSourceMeta;
  const quelle = meta ? `${meta.platform}-Post von ${meta.author}` : null;
  return { richtung, quelle };
}
